import { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../store';
import { useMobileContext } from '../layouts/MobileLayout';
import { getMdtEventId } from '../lib/mdtSessionStorage';
import { MapPin, AlertTriangle, Send, ChevronDown, Clock } from 'lucide-react';
import { cn, formatTimestamp, getSeverityColor, getSeverityBg, getCategoryIcon, API_BASE_URL } from '../lib/utils';
import type { Incident, IncidentLog, MdtDispatch } from '../types';

export default function MobileIncidentPage() {
  const { token } = useAppSelector((state) => state.auth);
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const { mdtResource } = useMobileContext();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [logs, setLogs] = useState<IncidentLog[]>([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState(false);

  const mdtEventId = getMdtEventId(mdtResource);
  const acceptedAssignment = activeDispatch?.status === 'accepted' ? activeDispatch : null;

  const resolveIncidentId = useCallback(async (): Promise<string | null> => {
    if (!mdtResource || !token) return null;

    if (acceptedAssignment?.incidentId) {
      if (!mdtEventId || !acceptedAssignment.eventId || acceptedAssignment.eventId === mdtEventId) {
        return acceptedAssignment.incidentId;
      }
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/dispatches?targetUnitId=${encodeURIComponent(mdtResource.id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return null;
      const data = await res.json();
      const accepted = (data.dispatches as MdtDispatch[] || []).find(
        (d) =>
          d.status === 'accepted' &&
          (!mdtEventId || !d.eventId || d.eventId === mdtEventId)
      );
      return accepted?.incidentId ?? null;
    } catch {
      return null;
    }
  }, [acceptedAssignment, mdtEventId, mdtResource, token]);

  const fetchIncident = useCallback(async () => {
    if (!token || !mdtResource) {
      setIncident(null);
      setLogs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const incidentId = await resolveIncidentId();
      if (!incidentId) {
        setIncident(null);
        setLogs([]);
        return;
      }

      const res = await fetch(`${API_BASE_URL}/incidents/${incidentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setIncident(null);
        setLogs([]);
        return;
      }

      const data = await res.json();
      const fetched: Incident | null = data.incident ?? null;
      if (!fetched || (mdtEventId && fetched.eventId && fetched.eventId !== mdtEventId)) {
        setIncident(null);
        setLogs([]);
        return;
      }

      setIncident(fetched);
      setLogs(fetched.logs || []);
    } catch (error) {
      console.error('Failed to fetch incident:', error);
      setIncident(null);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [token, mdtResource, mdtEventId, resolveIncidentId]);

  useEffect(() => {
    fetchIncident();
  }, [fetchIncident]);

  const addLog = async () => {
    if (!incident || !newNote.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/incidents/${incident.id}/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'Note Added',
          details: newNote.trim(),
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setLogs([...logs, data.log]);
        setNewNote('');
      }
    } catch (error) {
      console.error('Failed to add log:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white/60 text-sm">Loading incident...</div>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <AlertTriangle className="w-12 h-12 text-yellow-400 mb-3" />
        <div className="text-white/80 text-sm font-semibold mb-1">No Active Assignment</div>
        <div className="text-white/50 text-xs text-center">
          You are not currently assigned to any incident for this event
        </div>
      </div>
    );
  }

  const displayLogs = expandedLogs ? logs : logs.slice(-5);

  return (
    <div className="flex flex-col h-full overflow-auto p-4 space-y-4">
      {/* Incident Header */}
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{getCategoryIcon(incident.category)}</span>
            <span className="text-xs font-mono text-muted-foreground">{incident.id}</span>
            <span
              className={cn(
                'text-[10px] px-1.5 py-0.5 rounded-full border font-bold uppercase',
                getSeverityBg(incident.severity),
                getSeverityColor(incident.severity)
              )}
            >
              {incident.severity}
            </span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Clock className="w-3 h-3" />
            {formatTimestamp(incident.reportedAt)}
          </div>
        </div>

        <h2 className="text-sm font-bold text-white mb-2">{incident.title}</h2>

        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
          <MapPin className="w-3 h-3" />
          {incident.location.address || 'Unknown location'}
        </div>

        {incident.description && (
          <p className="text-xs text-white/70 leading-relaxed">
            {incident.description}
          </p>
        )}
      </div>

      {/* Logs Section */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold text-white/90">Incident Log</h3>
          {logs.length > 5 && (
            <button
              onClick={() => setExpandedLogs(!expandedLogs)}
              className="text-[10px] text-blue-400 flex items-center gap-1"
            >
              {expandedLogs ? 'Show less' : `Show all (${logs.length})`}
              <ChevronDown className={cn('w-3 h-3 transition-transform', expandedLogs && 'rotate-180')} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto space-y-2 mb-3">
          {displayLogs.length === 0 ? (
            <div className="text-center text-white/40 text-xs py-4">
              No logs yet
            </div>
          ) : (
            displayLogs.map((log) => (
              <div
                key={log.id}
                className="bg-card/30 border border-border rounded-lg p-3"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-blue-300">{log.action}</span>
                  <span className="text-[9px] text-muted-foreground">
                    {formatTimestamp(log.timestamp)}
                  </span>
                </div>
                {log.details && (
                  <p className="text-xs text-white/70 leading-relaxed">{log.details}</p>
                )}
                {log.userName && (
                  <div className="text-[9px] text-muted-foreground mt-1">
                    — {log.userName}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add Note Input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note..."
            className="flex-1 px-3 py-2 rounded-lg bg-card/50 border border-border text-xs text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            onKeyPress={(e) => e.key === 'Enter' && addLog()}
          />
          <button
            onClick={addLog}
            disabled={!newNote.trim()}
            className={cn(
              'px-3 py-2 rounded-lg transition-colors',
              newNote.trim()
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-white/10 text-white/40 cursor-not-allowed'
            )}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
